import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { Input } from "../../../../components/tailwind/input";
import { inspectorInputClass, errorInputStyle } from "./RightInspectorStyles";

export const keyValueToRows = (value) => {
  const entries = Object.entries(value || {});
  return entries.map(([key, rowValue]) => ({ key, value: String(rowValue ?? "") }));
};

export const rowsToKeyValue = (rows) =>
  rows.reduce((acc, row) => {
    if (!row.key) return acc;
    acc[row.key] = row.value;
    return acc;
  }, {});

export function KeyValueField({ value, onChange, field, showErrors }) {
  const isKeyOnly = field?.keyOnly === true;
  const [rows, setRows] = useState(() => {
    if (Array.isArray(value)) return value;
    return keyValueToRows(value);
  });
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    const externalRows = Array.isArray(value) ? value : keyValueToRows(value);
    const extNonEmpty = externalRows.filter(r => r.key);
    const localNonEmpty = rows.filter(r => r.key);
    
    const hasExternalDiff = extNonEmpty.length !== localNonEmpty.length || 
      extNonEmpty.some((extRow, i) => {
        const localRow = localNonEmpty[i];
        return !localRow || localRow.key !== extRow.key || (!isKeyOnly && localRow.value !== extRow.value);
      });

    if (hasExternalDiff) {
      setRows(externalRows);
    }
  }, [value, isKeyOnly]);

  const handleRowChange = (idx, keyOrVal, val) => {
    setError("");
    const next = [...rows];
    next[idx] = { ...next[idx], [keyOrVal]: val };
    setRows(next);
    onChange(next);
  };

  const handleAddRow = () => {
    const hasIncomplete = rows.some(row => !row.key.trim() || (!isKeyOnly && !row.value.trim()));
    if (hasIncomplete) {
      setError(
        isKeyOnly
          ? "Please fill in all keys before adding a new row."
          : "Please fill in all keys and values before adding a new row."
      );
      return;
    }
    setError("");
    const next = [...rows, { key: "", value: "" }];
    setRows(next);
    onChange(next);
  };

  const handleRemoveRow = (idx) => {
    setError("");
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {rows.map((row, idx) => {
        const isKeyError = showErrors && (!row.key || !row.key.trim());
        const isValError = showErrors && !isKeyOnly && (!row.value || !row.value.trim());
        return (
          <div className="flex items-center gap-2" key={`${field.name}-row-${idx}`}>
            <div className={isKeyOnly ? "grid grid-cols-1 gap-2 flex-1" : "grid grid-cols-2 gap-2 flex-1"}>
              <Input
                value={row.key}
                placeholder="Variable Name"
                className={inspectorInputClass}
                style={isKeyError ? errorInputStyle : undefined}
                onChange={(e) => handleRowChange(idx, "key", e.target.value)}
              />
              {!isKeyOnly && (
                <Input
                  value={row.value}
                  placeholder="value"
                  className={inspectorInputClass}
                  style={isValError ? errorInputStyle : undefined}
                  onChange={(e) => handleRowChange(idx, "value", e.target.value)}
                />
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 shrink-0"
              onClick={() => handleRemoveRow(idx)}
              aria-label="Remove row"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        className="w-full h-8 text-xs"
        onClick={handleAddRow}
      >
        Add row
      </Button>
      {error && (
        <p className="text-xs text-red-500 font-medium dark:text-red-400 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
